import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PAGE_SIZE } from '../../classes/grid-page-info';
import {
  GQLArguments,
  GQLResponse,
  IGQLResponse,
  IGQLSingleResponse,
} from '../../interfaces/graphql.interface';
import {
  IGQLUsersParams,
  IUser,
  IUserAccountDTO,
  IUserByGroupParams,
  IUserByGroupResponse,
  IUserGQL,
  IUsersPermissionsParams,
  IUserWithPermissions,
  UserFilters,
} from '../../interfaces/user/user.interface';
import {
  getLogginedUserQuery,
  getUserQuery,
  getUsersByGroups,
  getUsersPermissionsQuery,
  getUsersQuery,
  getUsersWithSameAccessQuery,
} from '../../interfaces/user/user.qraphql';
import { HelperGQLService } from '../helper.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class UserGQLService {
  constructor(
    private apollo: Apollo,
    private gqlService: HelperGQLService,
  ) {}

  getUsers(
    usersParams?: IGQLUsersParams,
    withSameAccessOnly = false,
  ): Observable<GQLResponse<IUser>> {
    const { orderby, direction, filter, after } = usersParams ?? {};
    const sortBy = orderby;
    const sortDirection = direction;

    const gqlArguments: GQLArguments = {
      first: 30,
    };

    if (filter) {
      this.setUserFilters(gqlArguments, filter);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IUserGQL>>({
        query: withSameAccessOnly ? getUsersWithSameAccessQuery : getUsersQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return this.mapUsersResponse(
            withSameAccessOnly
              ? response.data.usersWithSameAccess
              : response.data.users,
          );
        }),
      );
  }

  private setUserFilters(gqlArguments: GQLArguments, filter: UserFilters) {
    const userFilters = {
      firstName: { contains: filter.firstName },
      lastName: { contains: filter.lastName },
      userName: { contains: filter.userName },
      ext: { contains: filter.ext },
      groupId: { contains: filter.groupId },
      accessLevel: { eq: filter.accessLevel },
      active: filter.active
        ? { eq: filter.active === 'Active' ? true : false }
        : null,
      passwordResetAllowed: filter.passwordResetAllowed
        ? { eq: filter.passwordResetAllowed === 'Active' ? true : false }
        : null,
    };

    const cleanFilters = this.gqlService.cleanFilterObject(userFilters);

    if (cleanFilters) {
      gqlArguments.where = cleanFilters;
    }
  }

  getUser(id: number): Observable<IUser> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IUserGQL>>({
        query: getUserQuery,
        fetchPolicy: 'no-cache',
        variables: {
          id,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return this.mapUser(response.data.user);
        }),
      );
  }

  getLoggedInUser(): Observable<IUser> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IUserGQL>>({
        query: getLogginedUserQuery,
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(
        map((response) => {
          return this.mapUser(response.data.loggedInUser);
        }),
      );
  }

  getUsersByGroups(
    userByGroupParams: IUserByGroupParams,
  ): Observable<GQLResponse<IUserByGroupResponse>> {
    const { filter, after, groups } = userByGroupParams;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gqlArguments: any = {
      groups,
      first: PAGE_SIZE,
    };

    if (filter) {
      const userFilters = {
        firstName: filter.firstName ? { contains: filter.firstName } : null,
        lastName: filter.lastName ? { contains: filter.lastName } : null,
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(userFilters);
    }

    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IUserByGroupResponse>>({
        query: getUsersByGroups,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.usersByGroup;
        }),
      );
  }

  getUsersPermissions(
    params?: IUsersPermissionsParams,
  ): Observable<GQLResponse<IUserWithPermissions>> {
    const gqlArguments: GQLArguments = {
      first: 30,
    };

    if (params?.filter) {
      this.setUserFilters(gqlArguments, params.filter);
    }

    if (params?.after) {
      gqlArguments.after = params.after;
    }

    return this.apollo
      .query<IGQLResponse<IUserWithPermissions>>({
        query: getUsersPermissionsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .pipe(
        map((response) => {
          const users = response.data.usersWithSameAccess.nodes;
          const allowedPermissionsIds = params?.allowedPermissionsIds ?? [];

          const filteredUsers = users.filter((user) => {
            return (
              user.permissions.length > 0 &&
              user.permissions.every((x) => {
                return allowedPermissionsIds.includes(x.permissionId);
              })
            );
          });

          response.data.usersWithSameAccess.nodes = filteredUsers;

          return response.data.usersWithSameAccess;
        }),
      );
  }

  private mapUser(user: IUserGQL): IUser {
    try {
      const u = {
        ...user,
        applications: user.applications?.map((x) => {
          return x.application;
        }),
        accounts: user.accounts?.map((x) => {
          return {
            accountNumber: x.accountNumber,
            name: x.account.name,
          } as IUserAccountDTO;
        }),
        groups: user.groups?.map((x) => {
          return x.group;
        }),
      } as IUser;

      return u;
    } catch (error) {
      console.error(error);
      return error;
    }
  }

  private mapUsersResponse(data: GQLResponse<IUserGQL>): GQLResponse<IUser> {
    const result = {
      pageInfo: data.pageInfo,
      nodes: data.nodes.map((x) => {
        return this.mapUser(x);
      }),
    } as GQLResponse<IUser>;

    return result;
  }
}
